/**
 * Hand Evaluator for Texas Hold'em Poker
 *
 * Evaluates the best 5-card poker hand from up to 7 cards,
 * classifies it into one of 10 hand ranks, generates a comparison
 * key for deterministic ordering, and provides human-readable descriptions.
 *
 * Requirements: 8.1, 8.2, 8.3, 8.4, 8.5, 8.6, 9.5
 */

import { Card, HandRank, HandResult, RANK_VALUES, Rank } from './types';

// ============================================================================
// Internal helpers
// ============================================================================

/** Get numeric value for a rank */
function rankValue(rank: Rank): number {
  return RANK_VALUES[rank];
}

/** Human-readable rank name */
function rankName(rank: Rank): string {
  const names: Record<Rank, string> = {
    '2': 'Two', '3': 'Three', '4': 'Four', '5': 'Five',
    '6': 'Six', '7': 'Seven', '8': 'Eight', '9': 'Nine',
    '10': 'Ten', 'J': 'Jack', 'Q': 'Queen', 'K': 'King', 'A': 'Ace',
  };
  return names[rank];
}

/** Plural rank name for descriptions */
function rankNamePlural(rank: Rank): string {
  const plurals: Record<Rank, string> = {
    '2': 'Twos', '3': 'Threes', '4': 'Fours', '5': 'Fives',
    '6': 'Sixes', '7': 'Sevens', '8': 'Eights', '9': 'Nines',
    '10': 'Tens', 'J': 'Jacks', 'Q': 'Queens', 'K': 'Kings', 'A': 'Aces',
  };
  return plurals[rank];
}

/**
 * Generate all C(n, k) combinations of elements from an array.
 */
function combinations<T>(arr: T[], k: number): T[][] {
  if (k === 0) return [[]];
  if (arr.length < k) return [];
  const result: T[][] = [];

  function backtrack(start: number, current: T[]) {
    if (current.length === k) {
      result.push([...current]);
      return;
    }
    for (let i = start; i < arr.length; i++) {
      current.push(arr[i]);
      backtrack(i + 1, current);
      current.pop();
    }
  }

  backtrack(0, []);
  return result;
}

/**
 * Build a frequency map of ranks in a hand.
 * Returns entries sorted by frequency desc, then rank value desc.
 */
function getRankGroups(cards: Card[]): { rank: Rank; count: number }[] {
  const freq = new Map<Rank, number>();
  for (const card of cards) {
    freq.set(card.rank, (freq.get(card.rank) || 0) + 1);
  }
  return Array.from(freq.entries())
    .map(([rank, count]) => ({ rank, count }))
    .sort((a, b) => {
      if (b.count !== a.count) return b.count - a.count;
      return rankValue(b.rank) - rankValue(a.rank);
    });
}

/**
 * Check if 5 sorted cards form a straight.
 * Returns the high card value of the straight, or 0 if not a straight.
 * Handles ace-low straight (A-2-3-4-5) where the high card is 5.
 */
function getStraightHighCard(cards: Card[]): number {
  const values = cards.map(c => rankValue(c.rank)).sort((a, b) => b - a);

  // Check normal straight (descending consecutive)
  let isConsecutive = true;
  for (let i = 1; i < values.length; i++) {
    if (values[i - 1] - values[i] !== 1) {
      isConsecutive = false;
      break;
    }
  }
  if (isConsecutive) {
    return values[0]; // highest card
  }

  // Check ace-low straight: A-5-4-3-2 → values [14, 5, 4, 3, 2]
  if (
    values[0] === 14 &&
    values[1] === 5 &&
    values[2] === 4 &&
    values[3] === 3 &&
    values[4] === 2
  ) {
    return 5; // 5-high straight (ace plays low)
  }

  return 0;
}

/**
 * Check if all 5 cards share the same suit.
 */
function isFlush(cards: Card[]): boolean {
  return cards.every(c => c.suit === cards[0].suit);
}

// ============================================================================
// Core evaluation of a single 5-card hand
// ============================================================================

/**
 * Classify a single 5-card hand and generate its comparison key.
 */
function evaluate5(cards: Card[]): HandResult {
  const flush = isFlush(cards);
  const straightHigh = getStraightHighCard(cards);
  const straight = straightHigh > 0;
  const groups = getRankGroups(cards);

  let rank: HandRank;
  let comparisonKey: number[];

  if (flush && straight) {
    if (straightHigh === 14) {
      // Royal Flush: A-K-Q-J-10 all same suit
      rank = HandRank.ROYAL_FLUSH;
      comparisonKey = [HandRank.ROYAL_FLUSH, 14];
    } else {
      // Straight Flush
      rank = HandRank.STRAIGHT_FLUSH;
      comparisonKey = [HandRank.STRAIGHT_FLUSH, straightHigh];
    }
  } else if (groups[0].count === 4) {
    // Four of a Kind
    rank = HandRank.FOUR_OF_A_KIND;
    const quadRank = rankValue(groups[0].rank);
    const kicker = rankValue(groups[1].rank);
    comparisonKey = [HandRank.FOUR_OF_A_KIND, quadRank, kicker];
  } else if (groups[0].count === 3 && groups[1].count === 2) {
    // Full House
    rank = HandRank.FULL_HOUSE;
    const tripRank = rankValue(groups[0].rank);
    const pairRank = rankValue(groups[1].rank);
    comparisonKey = [HandRank.FULL_HOUSE, tripRank, pairRank];
  } else if (flush) {
    // Flush (not a straight)
    rank = HandRank.FLUSH;
    const sorted = cards
      .map(c => rankValue(c.rank))
      .sort((a, b) => b - a);
    comparisonKey = [HandRank.FLUSH, ...sorted];
  } else if (straight) {
    // Straight (not a flush)
    rank = HandRank.STRAIGHT;
    comparisonKey = [HandRank.STRAIGHT, straightHigh];
  } else if (groups[0].count === 3) {
    // Three of a Kind
    rank = HandRank.THREE_OF_A_KIND;
    const tripRank = rankValue(groups[0].rank);
    const kickers = groups
      .slice(1)
      .map(g => rankValue(g.rank))
      .sort((a, b) => b - a);
    comparisonKey = [HandRank.THREE_OF_A_KIND, tripRank, ...kickers];
  } else if (groups[0].count === 2 && groups[1].count === 2) {
    // Two Pair
    rank = HandRank.TWO_PAIR;
    const pairValues = [rankValue(groups[0].rank), rankValue(groups[1].rank)].sort(
      (a, b) => b - a
    );
    const kicker = rankValue(groups[2].rank);
    comparisonKey = [HandRank.TWO_PAIR, ...pairValues, kicker];
  } else if (groups[0].count === 2) {
    // One Pair
    rank = HandRank.ONE_PAIR;
    const pairRank = rankValue(groups[0].rank);
    const kickers = groups
      .slice(1)
      .map(g => rankValue(g.rank))
      .sort((a, b) => b - a);
    comparisonKey = [HandRank.ONE_PAIR, pairRank, ...kickers];
  } else {
    // High Card
    rank = HandRank.HIGH_CARD;
    const sorted = cards
      .map(c => rankValue(c.rank))
      .sort((a, b) => b - a);
    comparisonKey = [HandRank.HIGH_CARD, ...sorted];
  }

  // Sort bestCards by rank descending for display, but for ace-low straight
  // keep the ace at the end
  let bestCards: Card[];
  if (straight && straightHigh === 5) {
    // Ace-low straight: order as 5-4-3-2-A
    bestCards = [...cards].sort((a, b) => {
      const va = rankValue(a.rank) === 14 ? 1 : rankValue(a.rank);
      const vb = rankValue(b.rank) === 14 ? 1 : rankValue(b.rank);
      return vb - va;
    });
  } else {
    bestCards = [...cards].sort((a, b) => rankValue(b.rank) - rankValue(a.rank));
  }

  return {
    rank,
    rankName: '', // Will be filled by describeHand or at the top level
    bestCards,
    comparisonKey,
  };
}

// ============================================================================
// Public API
// ============================================================================

/**
 * Compare two comparison keys lexicographically.
 * Returns -1 if key1 < key2, 0 if equal, 1 if key1 > key2.
 */
function compareKeys(key1: number[], key2: number[]): -1 | 0 | 1 {
  const len = Math.max(key1.length, key2.length);
  for (let i = 0; i < len; i++) {
    const v1 = key1[i] ?? 0;
    const v2 = key2[i] ?? 0;
    if (v1 > v2) return 1;
    if (v1 < v2) return -1;
  }
  return 0;
}

/**
 * Evaluate the best 5-card hand from up to 7 cards.
 * Examines all C(n, 5) combinations and returns the best one.
 *
 * @param cards - 5 to 7 cards (2 hole + up to 5 community)
 * @returns The best HandResult
 */
export function evaluate(cards: Card[]): HandResult {
  if (cards.length < 5) {
    throw new Error(`Need at least 5 cards to evaluate, got ${cards.length}`);
  }
  if (cards.length > 7) {
    throw new Error(`Cannot evaluate more than 7 cards, got ${cards.length}`);
  }

  const combos = combinations(cards, 5);
  let best: HandResult | null = null;

  for (const combo of combos) {
    const result = evaluate5(combo);
    if (best === null || compareKeys(result.comparisonKey, best.comparisonKey) > 0) {
      best = result;
    }
  }

  // Fill in the human-readable name
  best!.rankName = describeHand(best!);
  return best!;
}

/**
 * Compare two evaluated hands.
 * Returns 1 if hand1 wins, -1 if hand2 wins, 0 if tie.
 */
export function compare(hand1: HandResult, hand2: HandResult): number {
  return compareKeys(hand1.comparisonKey, hand2.comparisonKey);
}

/**
 * Find the best 5-card hand from hole cards + community cards.
 *
 * @param holeCards - Player's 2 hole cards
 * @param communityCards - 3–5 community cards
 * @returns The best HandResult
 */
export function findBestHand(
  holeCards: Card[],
  communityCards: Card[]
): HandResult {
  const allCards = [...holeCards, ...communityCards];
  return evaluate(allCards);
}

/**
 * Generate a human-readable description of a hand result.
 *
 * Examples:
 *   "Royal Flush"
 *   "Straight Flush, Nine high"
 *   "Four of a Kind, Kings"
 *   "Full House, Kings over Tens"
 *   "Flush, Ace high"
 *   "Straight, Ten high"
 *   "Three of a Kind, Jacks"
 *   "Two Pair, Aces and Sevens"
 *   "One Pair, Queens"
 *   "High Card, Ace"
 */
export function describeHand(result: HandResult): string {
  const groups = getRankGroups(result.bestCards);

  switch (result.rank) {
    case HandRank.ROYAL_FLUSH:
      return 'Royal Flush';

    case HandRank.STRAIGHT_FLUSH: {
      const highCard = result.bestCards[0];
      return `Straight Flush, ${rankName(highCard.rank)} high`;
    }

    case HandRank.FOUR_OF_A_KIND: {
      const quadRank = groups[0].rank;
      return `Four of a Kind, ${rankNamePlural(quadRank)}`;
    }

    case HandRank.FULL_HOUSE: {
      const tripRank = groups[0].rank;
      const pairRank = groups[1].rank;
      return `Full House, ${rankNamePlural(tripRank)} over ${rankNamePlural(pairRank)}`;
    }

    case HandRank.FLUSH: {
      const highCard = result.bestCards[0];
      return `Flush, ${rankName(highCard.rank)} high`;
    }

    case HandRank.STRAIGHT: {
      // For ace-low straight, the high card value is 5
      const straightHigh = getStraightHighCard(result.bestCards);
      // Find the rank name for the high card value
      const highRank = result.bestCards.find(
        c => rankValue(c.rank) === straightHigh || (straightHigh === 5 && rankValue(c.rank) === 5)
      );
      if (highRank) {
        return `Straight, ${rankName(highRank.rank)} high`;
      }
      return `Straight`;
    }

    case HandRank.THREE_OF_A_KIND: {
      const tripRank = groups[0].rank;
      return `Three of a Kind, ${rankNamePlural(tripRank)}`;
    }

    case HandRank.TWO_PAIR: {
      const pair1 = groups[0].rank;
      const pair2 = groups[1].rank;
      // Show higher pair first
      const [high, low] =
        rankValue(pair1) > rankValue(pair2) ? [pair1, pair2] : [pair2, pair1];
      return `Two Pair, ${rankNamePlural(high)} and ${rankNamePlural(low)}`;
    }

    case HandRank.ONE_PAIR: {
      const pairRank = groups[0].rank;
      return `One Pair, ${rankNamePlural(pairRank)}`;
    }

    case HandRank.HIGH_CARD: {
      const highCard = result.bestCards[0];
      return `High Card, ${rankName(highCard.rank)}`;
    }

    default:
      return 'Unknown Hand';
  }
}
